import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Inbox, Loader2 } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import axios from "axios";

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
});

type FormData = z.infer<typeof formSchema>;

export default function Newsletter() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const { isSubmitting } = form.formState;

  const onSubmit = async (data: FormData) => {
    setStatus("idle");
    setMessage("");

    try {
      await axios.post("/api/subscribe", data);
      setStatus("success");
      setMessage(
        "Thanks for subscribing! You'll receive a personal email from me to say hi."
      );
      form.reset();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setStatus("error");
      setMessage(
        error.response?.data?.error || "Something went wrong. Please try again."
      );
    }
  };

  return (
    <div
      className="flex flex-col space-y-4 sm:space-y-6 w-full py-12 md:py-16"
    >
      <div className="space-y-2">
        <h2 className="text-lg max-w-fit font-semibold text-zinc-900 dark:text-zinc-100">
          Newsletter
        </h2>

        <p className="text-sm text-left max-w-fit text-zinc-500 dark:text-zinc-400">
          Subscribe for updates on web performance and engineering
        </p>
      </div>

      {status === "success" ? (
        <div className="text-green-600 dark:text-green-400 flex text-left items-center gap-2 animate-in fade-in">
          ✨ {message}
        </div>
      ) : (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col w-full max-w-md gap-2"
          >
            <div className="flex flex-col sm:flex-row gap-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        placeholder="First name"
                        disabled={isSubmitting}
                        className="bg-zinc-100/50 dark:bg-zinc-800/50 text-zinc-900
                         dark:text-zinc-100 dark:border-zinc-700/80 dark:hover:border-zinc-600/80"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        placeholder="Last name"
                        disabled={isSubmitting}
                        className="bg-zinc-100/50 dark:bg-zinc-800/50 text-zinc-900
                         dark:text-zinc-100 dark:border-zinc-700/80 dark:hover:border-zinc-600/80"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="name@email.com"
                        disabled={isSubmitting}
                        className="bg-zinc-100/50 dark:bg-zinc-800/50 text-zinc-900
                         dark:text-zinc-100 dark:border-zinc-700/80 dark:hover:border-zinc-600/80"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full sm:w-auto"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  <>
                    Subscribe
                    <Inbox className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
            {status === "error" && (
              <p className="text-red-500 dark:text-red-400 text-sm mt-1 animate-in fade-in">
                {message}
              </p>
            )}
          </form>
        </Form>
      )}

    </div>
  );
}
